/**
 * Cloudflare API Client
 *
 * Cliente para comunicarse directamente con el Cloudflare Worker.
 * Maneja autenticación JWT y refresh automático de tokens.
 */

import {
  getCloudflareConfig,
  saveCloudflareConfig,
  getStoredTokens,
  storeTokens,
  clearAllStorage,
} from './storage';

// Tipos
export interface Email {
  id: string;
  user_email: string;
  sender: string;
  recipient: string;
  subject: string;
  body_plain: string;
  body_html: string;
  timestamp: number;
  folder: string;
  is_read: number;
  is_draft: number;
  is_html: number;
  tags: string | null;
  thread_id: string | null;
  in_reply_to: string | null;
  message_id: string | null;
  to_field: string | null;
  cc_field: string | null;
  bcc_field: string | null;
  attachment_count: number;
  has_calendar_invitation: number;
  calendar_event_id: string | null;
  attachments: Attachment[];
}

export interface Attachment {
  id: string;
  email_id: string;
  filename: string;
  content_type: string;
  size: number;
}

export interface CalendarEvent {
  id: string;
  user_email: string;
  title: string;
  description: string;
  start_time: number;
  end_time: number;
  location: string | null;
  attendees: string[] | null;
  color: string;
  all_day: boolean;
  reminder_minutes: number | null;
  recurrence_rule: string | null;
  recurrence_end: number | null;
  recurrence_parent_id: string | null;
  recurrence_exception_dates: number[] | null;
  created_at: number;
  updated_at: number;
}

export interface Notification {
  id: string;
  user_email: string;
  type: string;
  title: string;
  message: string | null;
  data: Record<string, any> | null;
  read: boolean;
  created_at: number;
}

export interface FolderCounts {
  [folder: string]: {
    total: number;
    unread: number;
  };
}

export interface StorageInfo {
  used_bytes: number;
  used_mb: number;
  used_gb: number;
  limit_bytes: number;
  limit_gb: number;
  percent_used: number;
  email_count: number;
  attachment_count: number;
  emails_by_folder: Array<{ folder: string; count: number }>;
}

export interface MailgunConfig {
  api_key: string;
  domain: string;
  from_email: string;
  from_name?: string;
  signature?: string;
  webhook_signing_key?: string;
}

export interface UserInfo {
  email: string;
  created_at: number;
  last_seen: number;
  has_password: boolean;
  has_mailgun_config: boolean;
}

export interface MeResponse {
  user: UserInfo;
  mailgun_config: MailgunConfig | null;
  stats: {
    email_count: number;
  };
}

export interface SendEmailData {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  is_html?: boolean;
  attachments?: Array<{ id: string; filename: string }>;
  in_reply_to?: string;
  thread_id?: string;
}

export interface BulkOperationData {
  action: 'delete' | 'mark_read' | 'mark_unread' | 'move' | 'add_tag' | 'remove_tag';
  email_ids: string[];
  folder?: string;
  tag?: string;
}

// Clase principal del cliente
class CloudflareApiClient {
  private workerUrl: string = '';
  private accessToken: string = '';
  private refreshToken: string = '';
  private tokenExpiry: number = 0;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<void> | null = null;

  /**
   * Parsea un JWT para obtener el payload (sin verificar firma)
   */
  private parseJwt(token: string): { exp?: number; sub?: string } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1];
      // Base64url decode
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Inicializa el cliente con la URL del worker y tokens
   */
  async initialize(): Promise<boolean> {
    try {
      // Obtener config desde localStorage
      const config = getCloudflareConfig();

      if (!config.worker_url) {
        console.warn('Worker URL not configured');
        return false;
      }

      this.workerUrl = config.worker_url.replace(/\/$/, ''); // Remove trailing slash

      // Obtener tokens almacenados
      const tokens = getStoredTokens();

      if (tokens.access_token) {
        this.accessToken = tokens.access_token;
        // Parsear JWT para obtener expiración real
        const payload = this.parseJwt(tokens.access_token);
        if (payload?.exp) {
          this.tokenExpiry = payload.exp * 1000; // exp está en segundos
        } else {
          // Fallback: asumir que expira pronto para forzar refresh
          this.tokenExpiry = Date.now() + 60 * 1000;
        }
      }

      if (tokens.refresh_token) {
        this.refreshToken = tokens.refresh_token;
      }

      // Si el access token ya expiró pero tenemos refresh token, intentar refrescar
      if (this.refreshToken && (!this.accessToken || Date.now() >= this.tokenExpiry)) {
        try {
          await this.refreshAccessToken();
        } catch (error) {
          console.warn('Failed to refresh token on init:', error);
          // Continuar sin tokens - el usuario verá error y deberá re-loguearse
        }
      }

      return !!this.accessToken;
    } catch (error) {
      console.error('Failed to initialize CloudflareApi:', error);
      return false;
    }
  }

  /**
   * Actualiza los tokens (usado después de login/refresh)
   */
  setTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.tokenExpiry = Date.now() + (expiresIn * 1000) - 60000; // 1 min before expiry
  }

  /**
   * Verifica si el token está por expirar y lo refresca si es necesario
   */
  private async ensureValidToken(): Promise<void> {
    if (!this.accessToken) {
      throw new Error('No hay sesión activa');
    }

    // Si el token expira en menos de 2 minutos, refrescarlo
    if (Date.now() > this.tokenExpiry - 120000) {
      await this.refreshAccessToken();
    }
  }

  /**
   * Refresca el access token usando el refresh token
   * Maneja llamadas concurrentes para evitar múltiples refreshes simultáneos
   */
  private async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    // Si ya hay un refresh en progreso, esperar a que termine
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<void> {
    try {
      console.log('🔄 Refreshing access token...');
      const response = await fetch(`${this.workerUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        // Clear tokens on refresh failure
        this.accessToken = '';
        this.refreshToken = '';
        this.tokenExpiry = 0;
        throw new Error(error.error || 'Failed to refresh token');
      }

      const data = await response.json() as {
        access_token: string;
        refresh_token: string;
        expires_in: number;
      };

      this.accessToken = data.access_token;
      this.refreshToken = data.refresh_token;
      this.tokenExpiry = Date.now() + (data.expires_in * 1000) - 60000;

      // Guardar nuevos tokens en localStorage
      storeTokens(data.access_token, data.refresh_token);

      console.log('✅ Token refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw new Error('Sesión expirada. Por favor inicia sesión de nuevo.');
    }
  }

  /**
   * Realiza una petición autenticada al Worker
   * Reintenta una vez si recibe 401 (token expirado)
   */
  private async request<T>(
    path: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    await this.ensureValidToken();

    const response = await fetch(`${this.workerUrl}${path}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Si recibimos 401 y no es un retry, intentar refrescar y reintentar
    if (response.status === 401 && !isRetry && this.refreshToken) {
      console.warn('🔄 Got 401, attempting token refresh and retry...');
      try {
        // Forzar refresh aunque el token no parezca expirado
        this.tokenExpiry = 0;
        await this.refreshAccessToken();
        // Reintentar la petición
        return this.request<T>(path, options, true);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        throw new Error('Sesión expirada. Por favor inicia sesión de nuevo.');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || 'Request failed');
    }

    return response.json();
  }

  // ==========================================
  // MÉTODOS DE EMAILS
  // ==========================================

  /**
   * Lista emails con paginación y filtros
   */
  async listEmails(options: {
    folder?: string;
    limit?: number;
    offset?: number;
    since?: number;
  } = {}): Promise<{ emails: Email[]; total: number; has_more: boolean }> {
    const params = new URLSearchParams();
    if (options.folder) params.set('folder', options.folder);
    if (options.limit) params.set('limit', options.limit.toString());
    if (options.offset) params.set('offset', options.offset.toString());
    if (options.since) params.set('since', options.since.toString());

    const query = params.toString();
    return this.request(`/emails${query ? `?${query}` : ''}`);
  }

  /**
   * Obtiene un email individual con cuerpo completo
   */
  async getEmail(id: string): Promise<{ email: Email }> {
    return this.request(`/emails/${id}`);
  }

  /**
   * Obtiene los emails del mismo hilo de conversación
   */
  async getEmailThread(id: string): Promise<{
    thread_id: string | null;
    emails: Array<{
      id: string;
      sender: string;
      recipient: string;
      subject: string;
      timestamp: number;
      folder: string;
      is_read: number;
      body_plain: string;
    }>;
  }> {
    return this.request(`/emails/${id}/thread`);
  }

  /**
   * Busca emails
   */
  async searchEmails(
    query: string,
    folder?: string,
    limit: number = 50
  ): Promise<{ emails: Email[]; total: number }> {
    const params = new URLSearchParams({ q: query, limit: limit.toString() });
    if (folder) params.set('folder', folder);
    return this.request(`/emails/search?${params}`);
  }

  /**
   * Obtiene conteos por carpeta
   */
  async getEmailCounts(): Promise<{ counts: FolderCounts }> {
    return this.request('/emails/counts');
  }

  /**
   * Envía un email via Mailgun
   */
  async sendEmail(data: SendEmailData): Promise<{ status: string; email_id: string; message_id: string }> {
    return this.request('/emails/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Crea o actualiza un email (para borradores)
   */
  async syncEmail(email: Partial<Email> & { id: string }): Promise<{ status: string; id: string }> {
    return this.request('/emails', {
      method: 'POST',
      body: JSON.stringify(email),
    });
  }

  /**
   * Actualiza estado de un email (read, folder, tags)
   */
  async updateEmailStatus(
    emailId: string,
    updates: { is_read?: boolean; folder?: string; tags?: string[] }
  ): Promise<{ status: string }> {
    return this.request(`/emails/${emailId}/status`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Elimina un email permanentemente
   */
  async deleteEmail(emailId: string): Promise<{ status: string }> {
    return this.request(`/emails/${emailId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Operaciones en lote
   */
  async bulkOperation(data: BulkOperationData): Promise<{ status: string; affected: number }> {
    return this.request('/emails/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==========================================
  // MÉTODOS DE ADJUNTOS
  // ==========================================

  /**
   * Obtiene URL para descargar un adjunto
   */
  async getAttachmentUrl(attachmentId: string): Promise<{
    url: string;
    filename: string;
    content_type: string;
    size: number;
    expires_in: number;
  }> {
    return this.request(`/attachments/${attachmentId}/url`);
  }

  /**
   * Descarga un adjunto directamente (retorna blob)
   */
  async downloadAttachment(attachmentId: string): Promise<Blob> {
    await this.ensureValidToken();

    const response = await fetch(`${this.workerUrl}/attachments/${attachmentId}`, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download attachment');
    }

    return response.blob();
  }

  /**
   * Sube un adjunto a R2
   */
  async uploadAttachment(
    file: File,
    emailId: string
  ): Promise<{ attachment_id: string; filename: string; size: number }> {
    await this.ensureValidToken();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    formData.append('email_id', emailId);

    const response = await fetch(`${this.workerUrl}/attachments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Failed to upload attachment');
    }

    return response.json();
  }

  // ==========================================
  // MÉTODOS DE CARPETAS
  // ==========================================

  /**
   * Lista carpetas personalizadas
   */
  async getFolders(): Promise<{ folders: string[] }> {
    return this.request('/folders');
  }

  /**
   * Crea una carpeta personalizada
   */
  async createFolder(name: string): Promise<{ status: string; name: string }> {
    return this.request('/folders', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  /**
   * Elimina una carpeta personalizada
   */
  async deleteFolder(name: string): Promise<{ status: string }> {
    return this.request(`/folders/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  // ==========================================
  // MÉTODOS DE CALENDARIO
  // ==========================================

  /**
   * Lista eventos de calendario
   */
  async listCalendarEvents(options: {
    start?: number;
    end?: number;
  } = {}): Promise<{ events: CalendarEvent[] }> {
    const params = new URLSearchParams();
    if (options.start) params.set('start', options.start.toString());
    if (options.end) params.set('end', options.end.toString());

    const query = params.toString();
    return this.request(`/calendar${query ? `?${query}` : ''}`);
  }

  /**
   * Crea un evento de calendario
   */
  async createCalendarEvent(event: Omit<CalendarEvent, 'id' | 'user_email' | 'created_at' | 'updated_at'> & { send_invitations?: boolean }): Promise<{
    status: string;
    id: string;
    invitations_sent?: boolean;
    invitation_error?: string;
  }> {
    return this.request('/calendar', {
      method: 'POST',
      body: JSON.stringify(event),
    });
  }

  /**
   * Actualiza un evento de calendario
   */
  async updateCalendarEvent(
    eventId: string,
    updates: Partial<Omit<CalendarEvent, 'id' | 'user_email' | 'created_at' | 'updated_at'>>
  ): Promise<{ status: string }> {
    return this.request(`/calendar/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Elimina un evento de calendario
   */
  async deleteCalendarEvent(eventId: string): Promise<{ status: string }> {
    return this.request(`/calendar/${eventId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Crea un evento de calendario desde contenido ICS
   */
  async createEventFromIcs(
    icsContent: string,
    emailId?: string
  ): Promise<{
    status: string;
    id: string;
    title: string;
    start_time: number;
    end_time: number;
    location?: string;
    attendees?: string[];
  }> {
    return this.request('/calendar/from-ics', {
      method: 'POST',
      body: JSON.stringify({
        ics_content: icsContent,
        email_id: emailId,
      }),
    });
  }

  // ==========================================
  // MÉTODOS DE ALMACENAMIENTO
  // ==========================================

  /**
   * Obtiene información de almacenamiento
   */
  async getStorageInfo(): Promise<StorageInfo> {
    return this.request('/storage');
  }

  // ==========================================
  // MÉTODOS DE NOTIFICACIONES
  // ==========================================

  /**
   * Obtiene las notificaciones del usuario
   */
  async getNotifications(options?: {
    unread?: boolean;
    limit?: number;
  }): Promise<{
    notifications: Notification[];
    unread_count: number;
  }> {
    const params = new URLSearchParams();
    if (options?.unread) params.set('unread', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    const query = params.toString();
    return this.request(`/notifications${query ? `?${query}` : ''}`);
  }

  /**
   * Marca una notificación como leída
   */
  async markNotificationRead(notificationId: string): Promise<{ status: string; id: string }> {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'POST',
    });
  }

  /**
   * Verifica recordatorios pendientes y crea notificaciones
   * Debe llamarse periódicamente para detectar recordatorios
   */
  async checkReminders(): Promise<{
    checked: boolean;
    notifications_created: number;
    notifications: Array<{
      id: string;
      type: string;
      title: string;
      event_id: string;
      event_start_time: number;
    }>;
  }> {
    return this.request('/notifications/check-reminders', {
      method: 'POST',
    });
  }

  // ==========================================
  // MÉTODOS DE USUARIO Y CONFIGURACIÓN
  // ==========================================

  /**
   * Obtiene información del usuario actual y su config de Mailgun
   */
  async getMe(): Promise<MeResponse> {
    return this.request('/auth/me');
  }

  /**
   * Actualiza la configuración de Mailgun
   */
  async updateMailgunConfig(config: MailgunConfig): Promise<{ status: string }> {
    return this.request('/auth/config', {
      method: 'PUT',
      body: JSON.stringify({ mailgun_config: config }),
    });
  }

  // ==========================================
  // MÉTODOS DE AUTENTICACIÓN
  // ==========================================

  /**
   * Login con email y password
   */
  async login(email: string, password: string): Promise<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }> {
    const response = await fetch(`${this.workerUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      if (error.error === 'NEEDS_MIGRATION') {
        throw new Error('NEEDS_MIGRATION');
      }
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();

    // Guardar tokens en localStorage
    storeTokens(data.access_token, data.refresh_token);
    this.setTokens(data.access_token, data.refresh_token, data.expires_in);

    // Guardar email en config
    saveCloudflareConfig({ user_email: email });

    return data;
  }

  /**
   * Registro con password y config opcional de Mailgun
   */
  async register(
    email: string,
    password: string,
    workerUrl: string,
    mailgunConfig?: {
      api_key: string;
      domain: string;
      from_email: string;
      from_name?: string;
    }
  ): Promise<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }> {
    // Guardar worker URL primero
    saveCloudflareConfig({ worker_url: workerUrl, user_email: email });
    this.workerUrl = workerUrl.replace(/\/$/, '');

    const response = await fetch(`${this.workerUrl}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, mailgun_config: mailgunConfig }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Registration failed' }));
      throw new Error(error.error || 'Registration failed');
    }

    const data = await response.json();

    // Guardar tokens en localStorage
    storeTokens(data.access_token, data.refresh_token);
    this.setTokens(data.access_token, data.refresh_token, data.expires_in);

    return data;
  }

  /**
   * Migrar usuario legacy (API key a password)
   */
  async migrate(
    email: string,
    apiKey: string,
    newPassword: string
  ): Promise<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }> {
    const response = await fetch(`${this.workerUrl}/auth/migrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, api_key: apiKey, new_password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Migration failed' }));
      throw new Error(error.error || 'Migration failed');
    }

    const data = await response.json();

    storeTokens(data.access_token, data.refresh_token);
    this.setTokens(data.access_token, data.refresh_token, data.expires_in);
    saveCloudflareConfig({ user_email: email });

    return data;
  }

  /**
   * Logout - revocar tokens y limpiar almacenamiento
   */
  async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await fetch(`${this.workerUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
          },
        }).catch(() => {
          // Ignorar errores de red - limpiar localmente de todos modos
        });
      }
    } finally {
      // Limpiar todo localmente
      clearAllStorage();
      this.accessToken = '';
      this.refreshToken = '';
      this.tokenExpiry = 0;
    }
  }

  // ==========================================
  // GETTERS
  // ==========================================

  /**
   * Obtiene la URL del worker
   */
  getWorkerUrl(): string {
    return this.workerUrl;
  }

  /**
   * Establece la URL del worker (para login/register antes de inicializar)
   */
  setWorkerUrl(url: string): void {
    this.workerUrl = url.replace(/\/$/, '');
    saveCloudflareConfig({ worker_url: this.workerUrl });
  }

  /**
   * Obtiene el access token actual
   */
  getAccessToken(): string {
    return this.accessToken;
  }

  /**
   * Verifica si hay una sesión activa
   */
  isAuthenticated(): boolean {
    return !!this.accessToken && Date.now() < this.tokenExpiry;
  }
}

// Singleton
export const cloudflareApi = new CloudflareApiClient();

// Helper para inicializar y obtener la instancia
export async function initCloudflareApi(): Promise<boolean> {
  return cloudflareApi.initialize();
}

export default cloudflareApi;
